import type { ApolloError } from '@apollo/client';
import { type ErrorResponse, onError } from '@apollo/client/link/error';
import type { ServerParseError } from '@apollo/client/link/http';
import type { ServerError } from '@apollo/client/link/utils';
import * as Sentry from '@sentry/react';

import { history } from '@/components/root';
import { CREATE_LINKS_OPERATION_NAME } from '@/constant/gql';
import { ROUTE_LOGIN, ROUTE_UNAUTHORIZED } from '@/constant/routes';
import { isSiebelSessionExpired } from './siebelSessionExpired';

export default onError((props: ErrorResponse) => {
  const { graphQLErrors, networkError, operation } = props;

  if (isSiebelSessionExpired(graphQLErrors, networkError)) {
    return;
  }

  const isActionSubmission =
    operation?.operationName === CREATE_LINKS_OPERATION_NAME;

  if (networkError) {
    let finalErrorMessage = networkError.message;
    const { operationName } = operation;
    const queryVariables = operation.variables.input;

    console.error('networkError', networkError);

    if (isServerError(networkError)) {
      try {
        const { result } = networkError;
        if (typeof result === 'string') {
          finalErrorMessage = result;
        } else if (Array.isArray(result)) {
          // Batched response: an array of results, each with its own errors.
          const allErrors = result
            .filter(r => r?.errors?.length > 0)
            .map(r => r.errors.reduce((acc, err) => acc + err.message, ''));
          finalErrorMessage = allErrors.join(', ');
        } else if (Array.isArray(result?.errors)) {
          // Single GraphQL response: an object with an `errors` array.
          finalErrorMessage = result.errors.map(err => err.message).join(', ');
        } else if (typeof result?.message === 'string') {
          // Non-GraphQL error body (e.g. a 401 from auth middleware).
          finalErrorMessage = result.message;
        }
      } catch (e) {
        console.warn('Error parsing network error', e, networkError);
      }
    }

    Sentry.setContext('Stratumn GraphQL', {
      variables: JSON.stringify(queryVariables, null, 2),
      operationName
    });

    Sentry.captureMessage(finalErrorMessage);

    if (hasStatusCode(networkError)) {
      if (
        window.location.pathname === ROUTE_LOGIN &&
        networkError.statusCode === 401
      ) {
        return;
      }

      if (
        !isActionSubmission &&
        networkError.statusCode === 401 &&
        window.location.pathname !== ROUTE_LOGIN
      ) {
        // use second argument (state) to pass redirect variable
        history.push(ROUTE_LOGIN, {
          redirect: `${window.location.pathname}${window.location.search}`
        });
        return;
      }

      if (!isActionSubmission && networkError.statusCode === 403) {
        // redirect to the unauthorized page
        history.push(ROUTE_UNAUTHORIZED);
        return;
      }

      if (networkError.statusCode >= 500) {
        Sentry.captureMessage(finalErrorMessage, 'error');
        return;
      }
    }
  }

  if (graphQLErrors) {
    graphQLErrors.forEach(err => {
      const { message, locations, path } = err;
      console.error('GraphQL error', message, locations, path);

      // When receiving a 502 we refresh the current page.
      if (message === 'Response not successful: Received status code 502') {
        history.go(0);
        return;
      }

      if (
        'status' in err &&
        err.status === 401 &&
        window.location.pathname !== ROUTE_LOGIN
      ) {
        // use second argument (state) to pass redirect variable
        history.push(ROUTE_LOGIN, {
          redirect: `${window.location.pathname}${window.location.search}`
        });
        return;
      }

      if ('status' in err && err.status === 403) {
        // redirect to the unauthorized page
        history.push(ROUTE_UNAUTHORIZED);
        return;
      }

      if (
        'status' in err &&
        typeof err.status === 'number' &&
        err.status >= 500
      ) {
        Sentry.captureMessage(message, 'error');
      }
    });
  }
});

const isServerError = (
  error: ErrorResponse['networkError']
): error is ServerError => !!error && 'result' in error;

const hasStatusCode = (
  error: ErrorResponse['networkError']
): error is ServerError | ServerParseError => !!error && 'statusCode' in error;

export const is401Error = (error?: ApolloError): boolean => {
  // @ts-expect-error ApolloError is not typed
  return error?.cause?.statusCode === 401;
};
